/* eslint-disable import/order */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { HealthcareEntitiesService, FacilityList } from '../../../../api';
import { Pagination } from '../../../../app/components/Pagination/Pagination';
import { useSearchParamsState } from '../../../../app/hooks/search-params';
import { useDebounce } from '../../../../../v2/js/hooks/useDebounce';
import { healthcareEntityFacilityQueryKeys } from '../../../../app/query-keys-factories/facilities';
import 'react-range-slider-input/dist/style.css';
import styles from './FacilitiesList.module.scss';
import { SessionContext } from '../../../../app/contexts/SessionContext';
import { Button, Form } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs';
import { format, parseISO } from 'date-fns';
import { AddNewFacilityDialog } from '../../../../../v2/js/pages/Organization/AddNewFacilityDialog';
import { FacilityTypeEnum } from '../../../../api/models/FacilityTypeEnum';
import { toast } from 'react-toastify';
import { Drawer, DrawerBody } from '../../../../../v2/js/components/drawer';
import { FacilityDrawer } from '../../../../../v2/js/pages/Organization/FacilityDrawer';
import { useSearchParams } from 'react-router-dom';
import { default as clsx } from 'clsx';
import {
  MAIN_CONTENT_AREA,
  PAGE_HEADER_WITH_VERTICAL_MARGIN,
} from '../../../../../v2/js/utils/layoutStyles';
import { TRANSITION_ALL } from '../../../../../v2/js/utils/transitionStyles';
import { Heading } from '../../../../../v2/js/components/heading';
import { useDocumentTitle } from '../../../../app/hooks/use-document-title';

const PAGE_SIZE = 25;

interface FacilitiesListProps {
  showAddFacilityButton?: boolean;
}

const createColumns = (): ColumnDef<FacilityList>[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    size: 25,
    minSize: 0,
  },
  {
    header: 'Location',
    accessorFn: (row) => [row.city, row.state].filter(Boolean).join(', '),
    size: 20,
    minSize: 0,
  },
  {
    id: 'facility_type',
    header: 'Facility Type',
    accessorFn: (row) => window.FACILITY_TYPES[row.facility_type] || row.facility_type,
    cell: (props) => <div className="">{String(props.getValue())}</div>,
    size: 20,
    minSize: 0,
  },

  {
    id: 'emr',
    header: 'EMR',
    accessorFn: (row) => {
      const emrValue = row.emr;
      if (emrValue && typeof emrValue === 'string' && window.EMR_CHOICES) {
        return window.EMR_CHOICES[emrValue as keyof typeof window.EMR_CHOICES] || emrValue;
      }
      return emrValue || '';
    },
    cell: (props) => <div className="">{String(props.getValue())}</div>,
    size: 13,
    minSize: 0,
  },
  {
    id: 'updated_at',
    header: () => <div className="text-end">Last Updated</div>,
    accessorFn: (row) => {
      const dateValue = row.updated_at; // replace with actual field name
      return dateValue ? format(parseISO(dateValue), 'MMM d, yyyy') : '';
    },

    cell: (props) => <div className="text-end">{String(props.getValue())}</div>,
    size: 13,
    minSize: 0,
  },
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FacilitiesList = ({ showAddFacilityButton = false }: FacilitiesListProps) => {
  useDocumentTitle('Facilities List');
  const { user } = useContext(SessionContext);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const prevSearchRef = useRef(debouncedSearch);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Drawer state
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const canEditFacility = Boolean(user?.group_names?.includes('manage_facilities'));

  const [currentPageStr, setCurrentPage] = useSearchParamsState('page', '1');
  const currentPage = parseInt(currentPageStr);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      setCurrentPage('1');
      prevSearchRef.current = debouncedSearch;
    }
  }, [debouncedSearch, setCurrentPage]);

  // Check URL for facility ID and open drawer if present
  useEffect(() => {
    const facilityIdFromUrl = searchParams.get('id');
    if (facilityIdFromUrl) {
      setSelectedFacilityId(facilityIdFromUrl);
      setIsDrawerOpen(true);
    } else {
      // URL param was removed, close drawer only if it was open
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
        setSelectedFacilityId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleFacilityClick = useCallback(
    (facility: FacilityList) => {
      setSelectedFacilityId(facility.id);
      setIsDrawerOpen(true);
      // Update URL to include facility ID
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', facility.id);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedFacilityId(null);
    // Remove facility ID from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleAddFacility = async (data: { name: string; type: string }) => {
    try {
      const response = await HealthcareEntitiesService.healthcareEntitiesFacilitiesCreate({
        requestBody: {
          name: data.name,
          facility_type: data.type as FacilityTypeEnum,
        },
      });
      toast.success('Facility created successfully');
      setIsDialogOpen(false);
      // Invalidate facilities list query to refetch and show the new facility
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
      // Open drawer with the new facility
      setSelectedFacilityId(response.id);
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', response.id);
      setSearchParams(newSearchParams, { replace: true });
    } catch (error) {
      console.error('Failed to create facility:', error);
      toast.error('Failed to create facility. Please try again.');
    }
  };

  const params: Parameters<typeof HealthcareEntitiesService.healthcareEntitiesFacilitiesList>[0] = {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    search: debouncedSearch || undefined,
  };

  const queryFacilities = useQuery({
    queryKey: healthcareEntityFacilityQueryKeys.list(params),
    queryFn: async () => {
      return await HealthcareEntitiesService.healthcareEntitiesFacilitiesList(params);
    },
    keepPreviousData: true,
  });

  const columns = useMemo(() => createColumns(), []);

  const table = useReactTable({
    data: queryFacilities?.data?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleChangePage = (page: number) => {
    setCurrentPage(page.toString());
  };

  const renderItems = () => {
    if (queryFacilities.isLoading || queryFacilities.isFetching) {
      return <SkeletonTableRows />;
    }

    if (queryFacilities.isError) {
      return (
        <TableRow>
          <td colSpan={5} className="text-center">
            Error
          </td>
        </TableRow>
      );
    }

    if (queryFacilities.data?.count === 0) {
      return (
        <TableRow>
          <td colSpan={5} className="text-center py-4">
            {debouncedSearch ? (
              <div>
                <p className="text-muted mb-1">
                  No facilities found matching &ldquo;{debouncedSearch}&rdquo;
                </p>
                <small className="text-muted">Try adjusting your search terms</small>
              </div>
            ) : (
              <p className="text-muted">No facilities found</p>
            )}
          </td>
        </TableRow>
      );
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.id}
        onClick={() => handleFacilityClick(row.original)}
        style={{ cursor: 'pointer' }}
      >
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
        ))}
      </TableRow>
    ));
  };

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        {/* Main content area - takes 2/3 when drawer open, full width when closed */}
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5 tw:mr-3' : 'tw:w-full'
          )}
        >
          <main className={clsx(MAIN_CONTENT_AREA, 'tw:flex tw:flex-col')}>
            <div className={PAGE_HEADER_WITH_VERTICAL_MARGIN}>
              <Heading level={1} className="tw:!text-brand-green-0">
                Facilities
              </Heading>
              {canEditFacility && (
                <Button variant="primary" onClick={() => setIsDialogOpen(true)} className="ms-3">
                  Add Facility
                </Button>
              )}
            </div>
            <div>
              <Row className="justify-content-center" xs={'auto'}>
                <Col>
                  <Alert variant="danger" show={queryFacilities.isError}>
                    <Alert.Heading>Something went wrong</Alert.Heading>
                    <p>Please try again later. If the problem persists, please contact us.</p>
                  </Alert>
                </Col>
              </Row>
              <Row className="justify-content-center" xs={12}>
                <Col>
                  <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                    <div className="position-relative flex-grow-1">
                      <BsSearch className="position-absolute top-50 start-0 translate-middle-y text-muted ms-2" />
                      <Form.Control
                        type="text"
                        placeholder="Search name, city, state"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ paddingLeft: '2rem' }}
                      />
                    </div>
                    {showAddFacilityButton && canEditFacility && (
                      <Button
                        variant="primary"
                        onClick={() => setIsDialogOpen(true)}
                        className="ms-3"
                      >
                        Add Facility
                      </Button>
                    )}
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover align-middle">
                      <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                              <th
                                key={header.id}
                                colSpan={header.colSpan}
                                style={{
                                  width: `${header.getSize()}%`,
                                }}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody className="table-group-divider">{renderItems()}</tbody>
                    </table>
                  </div>

                  <div className="hstack gap-3 justify-content-center mb-4">
                    <span>
                      {queryFacilities.data?.count ?? 0}{' '}
                      {queryFacilities.data?.count === 1 ? 'facility' : 'facilities'}
                    </span>
                    <Pagination
                      count={queryFacilities.data?.count ?? 0}
                      pageSize={PAGE_SIZE}
                      currentPage={currentPage}
                      siblingCount={2}
                      onChangePage={handleChangePage}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </main>
        </div>

        {/* Drawer - side-by-side on desktop, modal on mobile */}
        {/* Drawer is outside main content area so it can extend to the top independently */}
        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedFacilityId && (
              <FacilityDrawer facilityId={selectedFacilityId} onClose={handleCloseDrawer} />
            )}
          </DrawerBody>
        </Drawer>
      </div>

      {canEditFacility && (
        <AddNewFacilityDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={handleAddFacility}
        />
      )}
    </>
  );
};

const TableRow = ({
  children,
  onClick,
  style,
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  style?: React.CSSProperties;
  'data-testid'?: string;
}) => (
  <tr onClick={onClick} style={style} {...props}>
    {children}
  </tr>
);

const SkeletonTableRows = () => {
  return Array(PAGE_SIZE)
    .fill(0)
    .map((_, index) => (
      <TableRow key={index} data-testid={`skeleton-row-${index}`}>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
        <td>
          <div className={styles.skeleton} />
        </td>
      </TableRow>
    ));
};
